import { useCallback, useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import SyncIcon from '@mui/icons-material/Sync';
import { api } from '../lib/api';
import { formatTimestamp } from '../lib/format';
import { PageHelp } from '../components/PageHelp';
import type { ImportOverview, ImportRun } from '../lib/types';

const STATUS_CHIP: Record<
  ImportRun['status'],
  { label: string; color: 'success' | 'error' | 'default' }
> = {
  ok: { label: 'OK', color: 'success' },
  error: { label: 'Fehler', color: 'error' },
  skipped: { label: 'übersprungen', color: 'default' },
};

function triggerLabel(trigger: string): string {
  if (trigger === 'schedule') return 'Zeitplan';
  if (trigger.startsWith('admin:')) return `manuell (${trigger.slice(6)})`;
  return trigger;
}

/** FR-10: Import-Protokoll und manueller Start (nur Admin). */
export default function ImportPage() {
  const [overview, setOverview] = useState<ImportOverview>();
  const [error, setError] = useState<string>();
  const [info, setInfo] = useState<string>();
  const [starting, setStarting] = useState(false);

  const load = useCallback(async () => {
    try {
      setOverview(await api.get<ImportOverview>('/admin/import'));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Import-Status konnte nicht geladen werden');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function start() {
    setError(undefined);
    setStarting(true);
    try {
      await api.post('/admin/import/run');
      setInfo('Import gestartet. Er dauert je nach Änderungen einige Sekunden bis Minuten.');
      window.setTimeout(() => void load(), 15000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Import konnte nicht gestartet werden');
    } finally {
      setStarting(false);
    }
  }

  if (!overview && !error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const status = overview?.status;
  return (
    <Stack spacing={3}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <Typography variant="h4">Import</Typography>
        <PageHelp text="Abgleich mit SevDesk: Belege des laufenden und des Vorjahres werden gelesen; nur neue oder geänderte Belege laden ihre Positionen nach. SevDesk wird dabei nie verändert." />
      </Stack>
      {error && <Alert severity="error">{error}</Alert>}
      {info && (
        <Alert severity="info" onClose={() => setInfo(undefined)}>
          {info}
        </Alert>
      )}
      {status && (
        <Stack spacing={0.5}>
          <Typography>
            Datenstand:{' '}
            {status.lastSuccessAt
              ? formatTimestamp(status.lastSuccessAt)
              : 'noch kein erfolgreicher Import'}
          </Typography>
          {status.lastError && status.lastErrorAt && (
            <Typography color="error">
              Letzter Fehler ({formatTimestamp(status.lastErrorAt)}): {status.lastError}
            </Typography>
          )}
        </Stack>
      )}
      <Stack direction="row" spacing={1}>
        <Button
          variant="contained"
          startIcon={<SyncIcon />}
          disabled={starting}
          onClick={() => void start()}
        >
          Jetzt importieren
        </Button>
        <Button onClick={() => void load()}>Aktualisieren</Button>
      </Stack>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Start</TableCell>
              <TableCell>Auslöser</TableCell>
              <TableCell>Ergebnis</TableCell>
              <TableCell>Jahre</TableCell>
              <TableCell align="right">Belege</TableCell>
              <TableCell align="right">geändert</TableCell>
              <TableCell align="right">geschrieben</TableCell>
              <TableCell align="right">entfernt</TableCell>
              <TableCell align="right">Dauer</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(overview?.runs ?? []).map((r) => (
              <TableRow key={r.startedAt} hover>
                <TableCell>{formatTimestamp(r.startedAt)}</TableCell>
                <TableCell>{triggerLabel(r.trigger)}</TableCell>
                <TableCell>
                  <Chip size="small" {...STATUS_CHIP[r.status]} />
                  {r.message && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {r.message}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>{r.years.join(', ')}</TableCell>
                <TableCell align="right">{r.vouchers ?? '—'}</TableCell>
                <TableCell align="right">{r.vouchersChanged ?? '—'}</TableCell>
                <TableCell align="right">{r.written ?? '—'}</TableCell>
                <TableCell align="right">{r.removed ?? '—'}</TableCell>
                <TableCell align="right">
                  {r.durationMs !== undefined ? `${(r.durationMs / 1000).toFixed(1)} s` : '—'}
                </TableCell>
              </TableRow>
            ))}
            {overview && overview.runs.length === 0 && (
              <TableRow>
                <TableCell colSpan={9}>
                  <Typography color="text.secondary">Noch kein Importlauf.</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
}
